'use strict';

/**
 * A page's own Scan Samples cart: opened into a host element, with either a sample it can't drop (the Sample page's
 * Location tab) or a location it can't change (a Location page's Samples tab adding samples to itself), or as a whole
 * page of its own (Scan Samples, #/ScanSamples), which keeps its settings on the device like the panels' cart.
 *
 * Each page has its own named cart instance (CartName), separate from the other page's and from the panels' Scan
 * Samples, and nothing is saved between opens: its "Save each scan" switch and location start fresh every time and
 * never touch the settings the panels' cart remembers on the device. The cart takes scans through HeadlightQR while
 * open, has the panels' camera button (the app's camera scanner, QRCodeProvider), and closes when the route leaves
 * the page's tab.
 *
 * Used by samples/SampleWorkspace-LocationTab.js, providers/LocationWorkspace-Provider.js and views/ScanSamplesPage.js.
 */

const libSampleScanning = require('headlight-sample-scanning');

/**
 * Storage that keeps nothing, so an embedded cart's switches and location never carry over to its next opening.
 * @return {{ getItem: (pKey: string) => null, setItem: () => void, removeItem: () => void }}
 */
function noStorage()
{
	return { getItem: () => null, setItem: () => null, removeItem: () => null };
}

/**
 * @typedef {Object} SampleLocationCartOptions
 * @property {() => any} Pict - the app's pict
 * @property {string} CartName - this page's own cart instance (provider, view and element prefix)
 * @property {string} HostID - the element the cart draws into
 * @property {() => string} OwnHash - the route of the page's tab; leaving it closes the cart
 * @property {(pMessage: string, pType: string) => void} Toast - the page's toast ('success' | 'error' | 'info')
 * @property {(pMove: { Location: Object, Moved: Array<Object>, Failed: Array<Object>, Via: string }) => any} [OnMoved]
 * @property {(pMark: { Received: Array<Object>, Failed: Array<Object> }) => any} [OnReceived] - Check Samples into Lab ran
 * @property {() => void} [OnLeave] - the route left the tab and the cart was closed
 * @property {Object} [Storage] - where the cart keeps its settings; default: nowhere (nothing is saved)
 * @property {boolean} [RememberSettings] - open with the stored "Save each scan" and location, as the panels' cart
 *     does, instead of collecting with no location
 * @property {(pSample: Object) => any} [PrintLabel] - adds the panels' print button to collected samples
 * @property {(pToast: { message: string, type: string }) => void} [Notify] - shows the cart's messages as they come
 *     (is-success / is-warning / ...), instead of through Toast
 */

class SampleLocationCartSession
{
	/**
	 * @param {SampleLocationCartOptions} pOptions
	 */
	constructor(pOptions)
	{
		this.options = pOptions;
		this.isOpen = false;
		this.cameraOpen = false;
		this._cameraToggling = false;
		this._onHashChange = null;
	}

	/** @return {any} */
	get pict()
	{
		return this.options.Pict();
	}

	/** @return {any} this page's cart provider, once mount has attached it */
	get cart()
	{
		return this.pict.providers[this.options.CartName];
	}

	/**
	 * Open the cart into the host, or redraw an open one into a freshly drawn host.
	 *
	 * @param {{ KeepSample?: Object, FixedLocation?: Object }} [pSetup] - a sample to preload that can't be removed, or
	 *     the only location samples can be moved into
	 */
	mount(pSetup)
	{
		const tmpDestination = `#${this.options.HostID}`;
		if (this.isOpen && this.cart)
		{
			// Redrawing the cart replaces the camera's box, so a running camera is closed first.
			this.closeCamera();
			this.cart.view.renderShell(tmpDestination);
			return;
		}
		const tmpSetup = pSetup || {};
		const tmpCart = new libSampleScanning(this.pict).attachInstance(this.options.CartName, { SampleScanning: { Storage: this.options.Storage || noStorage() } });
		const tmpHost =
		{
			notify: (pToast) => (this.options.Notify ? this.options.Notify(pToast) : this.options.Toast(pToast.message, SampleLocationCartSession.toastType(pToast.type))),
			moved: (pMove) => (this.options.OnMoved ? this.options.OnMoved(pMove) : null),
			received: (pMark) => (this.options.OnReceived ? this.options.OnReceived(pMark) : null),
			camera: { toggle: (pContainerID) => this.toggleCamera(pContainerID), isOpen: () => this.cameraOpen },
		};
		if (this.options.PrintLabel)
		{
			tmpHost.printLabel = (pSample) => this.options.PrintLabel(pSample);
		}
		tmpCart.connect(tmpHost);
		if (this.options.RememberSettings)
		{
			// As the panels' cart: the device's last "Save each scan" setting and remembered location.
			tmpCart.open({ DestinationAddress: tmpDestination });
		}
		else
		{
			// Every opening starts the same way: collecting, with no location unless the page fixes one.
			tmpCart.open({ DestinationAddress: tmpDestination, SaveEachScan: false });
			tmpCart.setLocation(null);
		}
		this.isOpen = true;
		if (tmpSetup.FixedLocation)
		{
			tmpCart.fixLocation(tmpSetup.FixedLocation);
		}
		if (tmpSetup.KeepSample)
		{
			tmpCart.keepSample(tmpSetup.KeepSample);
		}
		if (this.pict.providers.HeadlightQR)
		{
			tmpCart.listen(this.pict.providers.HeadlightQR);
		}
		this._watchForLeaving();
	}

	/** Close the cart: it stops taking scans, and its camera stops. */
	close()
	{
		this.closeCamera();
		if (this._onHashChange && (typeof window !== 'undefined'))
		{
			window.removeEventListener('hashchange', this._onHashChange);
		}
		this._onHashChange = null;
		if (this.isOpen && this.cart)
		{
			this.cart.close();
		}
		this.isOpen = false;
	}

	/** Close the cart when the route leaves the page's tab. */
	_watchForLeaving()
	{
		if ((typeof window === 'undefined') || this._onHashChange)
		{
			return;
		}
		const tmpOwnHash = this.options.OwnHash();
		this._onHashChange = () =>
		{
			if (window.location.hash !== tmpOwnHash)
			{
				this.close();
				if (this.options.OnLeave)
				{
					this.options.OnLeave();
				}
			}
		};
		window.addEventListener('hashchange', this._onHashChange);
	}

	/**
	 * The cart's camera button: open or close the app's camera scanner in the cart's camera box. What it reads
	 * reaches the cart through HeadlightQR, like a hand scanner's scans.
	 * @param {string} pContainerID
	 * @return {Promise<boolean>} whether the camera is now open
	 */
	async toggleCamera(pContainerID)
	{
		const tmpQRCode = this.pict.providers.QRCodeProvider;
		// One toggle at a time: opening the camera takes a moment.
		if (this._cameraToggling || !tmpQRCode)
		{
			return this.cameraOpen;
		}
		this._cameraToggling = true;
		this.cameraOpen = !this.cameraOpen;
		try
		{
			if (this.cameraOpen)
			{
				const tmpPanels = this.pict.services && this.pict.services.HeadlightPanels;
				await tmpQRCode.openScanner(pContainerID, { isPhoneDevice: !!(tmpPanels && tmpPanels.isPhoneDevice) });
			}
			else
			{
				await tmpQRCode.closeScanner();
			}
		}
		catch (pError)
		{
			this.pict.log.warn(`SampleLocationCart: the camera could not be ${this.cameraOpen ? 'opened' : 'closed'}: ${(pError && pError.message) || pError}`);
			if (this.cameraOpen)
			{
				this.cameraOpen = false;
				this.options.Toast('The camera could not be opened.', 'error');
			}
		}
		this._cameraToggling = false;
		return this.cameraOpen;
	}

	/** Stop the camera scanner if the cart has it open. */
	closeCamera()
	{
		if (!this.cameraOpen)
		{
			return;
		}
		this.cameraOpen = false;
		const tmpQRCode = this.pict.providers.QRCodeProvider;
		if (tmpQRCode && (typeof tmpQRCode.closeScanner === 'function'))
		{
			Promise.resolve(tmpQRCode.closeScanner()).catch((pError) => this.pict.log.warn(`SampleLocationCart: the camera could not be closed: ${(pError && pError.message) || pError}`));
		}
	}

	/**
	 * @param {string} pCartType - is-success / is-danger / is-warning / is-info
	 * @return {string} the page's toast type
	 */
	static toastType(pCartType)
	{
		if (pCartType === 'is-success')
		{
			return 'success';
		}
		return (pCartType === 'is-danger' || pCartType === 'is-warning') ? 'error' : 'info';
	}
}

module.exports = SampleLocationCartSession;
